import type { AddonActivation, AddonItemStat, QuizPreparation } from "types/";
import { analyticsTheme, HEAT_SHADES, type HeatShade } from "../../analyticsTheme";
import { PREVIEW_VERSION } from "../../constants/constants";

export const TAB_ADDONS = ["quiz", "summary", "flashcards", "standouts"] as const;
export type AddonKey = (typeof TAB_ADDONS)[number];

/** Every add-on of the tab but the quiz. */
export type NonQuizAddonKey = Exclude<AddonKey, "quiz">;

/** An add-on of the tab, as `visibleAddons` returns it. */
export type VisibleAddon = AddonActivation & { addon: AddonKey };

export const ADDON_COLORS: Record<AddonKey, string> = {
  // A deep violet: a lighter indigo could not be told apart from the blue.
  quiz: analyticsTheme.datasetViolet,
  summary: analyticsTheme.datasetAmber,
  flashcards: analyticsTheme.datasetBlue,
  standouts: analyticsTheme.datasetTeal,
};

/** Opened, interacted, completed: one teal ramp, light to dark, never near black. */
export const FUNNEL_COLORS = {
  opened: analyticsTheme.datasetTeal,
  interacted: analyticsTheme.datasetTealMid,
  completed: analyticsTheme.datasetTealDark,
} as const;

// Add-ons without a viewer surface, not tracked, or never generated have
// nothing to show in this tab. "disabled_by_creator" stays visible: sessions
// from before it was disabled still happened.
const HIDDEN_AVAILABILITY = new Set(["no_viewer_surface", "not_tracked", "not_generated"]);

export const percent = (num: number, den: number): number | null =>
  den > 0 ? (num / den) * 100 : null;

export const visibleAddons = (addons: AddonActivation[]): VisibleAddon[] =>
  TAB_ADDONS.flatMap((key) => {
    const addon = addons.find((a): a is VisibleAddon => a.addon === key);
    return addon && !HIDDEN_AVAILABILITY.has(addon.availability)
      ? [addon]
      : [];
  });

/** Active time per open, in seconds; null (a dash) when unknown or never opened. */
export const avgSecondsPerOpen = (a: AddonActivation): number | null =>
  a.active_seconds == null || a.sessions_opened === 0
    ? null
    : a.active_seconds / a.sessions_opened;

export const LOW_SAMPLE = 20;
/** Success rate (in percent) under which a quiz question is flagged as hard. */
export const HARD_QUESTION_RATE = 40;

export const QUESTION_TYPES = [
  "reading",
  "comprehension",
  "application",
  "analysis",
  "misconception",
];

/** A cohort is too small to read a difference from. */
export const isLowSample = (viewers: number): boolean =>
  viewers > 0 && viewers < LOW_SAMPLE;

export const itemRate = (item: AddonItemStat): number | null =>
  percent(item.correct ?? 0, item.touches);

export const isHardQuestion = (rate: number | null): boolean =>
  rate != null && rate < HARD_QUESTION_RATE;

/** Each item's sessions as a share of `base` (the first item's by default), capped at 100%. */
export const reachCurve = (
  items: AddonItemStat[],
  base: number = items[0]?.sessions ?? 0
): number[] => items.map((i) => (base > 0 ? Math.min(100, (i.sessions / base) * 100) : 0));

export type MatrixCell = { rate: number; touches: number; questions: number };
export type QuizMatrix = {
  types: string[];
  topics: string[];
  /** null topic or type = the margin over all topics or all types. */
  cell: (topic: string | null, type: string | null) => MatrixCell | null;
};

/** Null when no item carries both a question type and a topic. */
export const buildQuizMatrix = (items: AddonItemStat[]): QuizMatrix | null => {
  const typed = items.filter((i) => i.question_type != null && i.topic != null);
  if (typed.length === 0) return null;
  const present = new Set(typed.map((i) => i.question_type as string));
  const types = [
    ...QUESTION_TYPES.filter((t) => present.has(t)),
    ...[...present]
      .filter((t) => !QUESTION_TYPES.includes(t))
      .sort((a, b) => a.localeCompare(b)),
  ];
  const cell = (topic: string | null, type: string | null) => {
    const matching = typed.filter(
      (i) =>
        (topic == null || i.topic === topic) &&
        (type == null || i.question_type === type)
    );
    const touches = matching.reduce((sum, i) => sum + i.touches, 0);
    if (matching.length === 0 || touches === 0) return null;
    const correct = matching.reduce((sum, i) => sum + (i.correct ?? 0), 0);
    return { rate: (correct / touches) * 100, touches, questions: matching.length };
  };
  // Weakest topic first; the sort is stable, so ties keep their order.
  const topicRate = (topic: string) => cell(topic, null)?.rate ?? 0;
  const topics = [...new Set(typed.map((i) => i.topic as string))].sort(
    (a, b) => topicRate(a) - topicRate(b)
  );
  return { types, topics, cell };
};

export type QuizEffect = {
  avg: number;
  lift: number;
  viewers: number;
  baseline: number;
  baselineViewers: number;
  low: boolean;
};

/**
 * Null unless there is something to compare, takers who used the
 * add-on before the quiz and scored takers who went straight to it.
 */
export const quizEffect = (
  prep: QuizPreparation,
  addon: string
): QuizEffect | null => {
  const cohort = prep.prior_addons.find((c) => c.cohort === addon);
  const baseline = prep.quiz_first.avg_score;
  if (cohort?.avg_score == null) return null;
  if (baseline == null || prep.quiz_first.viewers === 0) return null;
  return {
    avg: cohort.avg_score,
    lift: Math.round(cohort.avg_score - baseline),
    viewers: cohort.viewers,
    baseline,
    baselineViewers: prep.quiz_first.viewers,
    low: isLowSample(cohort.viewers) || isLowSample(prep.quiz_first.viewers),
  };
};

/** Success-rate bands of the quiz heatmap, lowest first; each starts at `min` percent. */
export const SUCCESS_BANDS: (HeatShade & { min: number; label: string })[] = [
  { min: 0, label: "< 40%", ...HEAT_SHADES.worst },
  { min: HARD_QUESTION_RATE, label: "40–54%", ...HEAT_SHADES.bad },
  { min: 55, label: "55–69%", ...HEAT_SHADES.neutral },
  { min: 70, label: "70–84%", ...HEAT_SHADES.good },
  { min: 85, label: "≥ 85%", ...HEAT_SHADES.better },
];

export const successShade = (rate: number): HeatShade => {
  let shade: HeatShade = HEAT_SHADES.worst;
  for (const band of SUCCESS_BANDS) if (rate >= band.min) shade = band;
  return { background: shade.background, color: shade.color };
};

const addOptional = (a?: number | null, b?: number | null): number | undefined =>
  a == null && b == null ? undefined : (a ?? 0) + (b ?? 0);

/**
 * One row per item position. The backend lists an item once per version and
 * wording (several only under "all versions"): counters are summed and the
 * wording with the most touches is kept. A session that saw two wordings is
 * counted twice.
 */
export const mergeItems = (items: AddonItemStat[]): AddonItemStat[] => {
  const groups = new Map<number, [AddonItemStat, ...AddonItemStat[]]>();
  for (const item of items) {
    const group = groups.get(item.item_index);
    if (group) group.push(item);
    else groups.set(item.item_index, [item]);
  }
  return [...groups.values()]
    .map((rows) => {
      const main = rows.reduce((best, r) => (r.touches > best.touches ? r : best), rows[0]);
      const firstSet = (pick: (r: AddonItemStat) => string | null | undefined) =>
        rows.map(pick).find((v) => v != null) ?? null;
      const sumSet = (pick: (r: AddonItemStat) => number | null | undefined) =>
        rows.reduce<number | undefined>((sum, r) => addOptional(sum, pick(r)), undefined);
      return {
        ...main,
        label: main.label ?? firstSet((r) => r.label),
        question_type: firstSet((r) => r.question_type),
        topic: firstSet((r) => r.topic),
        touches: rows.reduce((sum, r) => sum + r.touches, 0),
        sessions: rows.reduce((sum, r) => sum + r.sessions, 0),
        correct: sumSet((r) => r.correct),
        flips: sumSet((r) => r.flips),
        known: sumSet((r) => r.known),
        review: sumSet((r) => r.review),
      };
    })
    .sort((a, b) => a.item_index - b.item_index);
};

/** What an endpoint other than the item stats knows about a question. */
export type QuestionMeta = Pick<AddonItemStat, "label" | "question_type" | "topic">;

/**
 * Every question of the pool once, in order, from the `mergeItems` output.
 * The backend only lists answered questions, so the missing positions are
 * filled with zeros, described from `known` when another endpoint knows the
 * question.
 */
export const quizQuestions = (
  positions: AddonItemStat[],
  poolSize: number | null | undefined,
  known: Map<number, QuestionMeta> = new Map()
): AddonItemStat[] => {
  const byIndex = new Map(positions.map((i) => [i.item_index, i]));
  // A detail response holds one version: the filled questions belong to it.
  const version = positions[0]?.project_publication_id ?? PREVIEW_VERSION;
  for (let index = 0; index < (poolSize ?? 0); index++) {
    if (!byIndex.has(index)) {
      const meta = known.get(index);
      byIndex.set(index, {
        item_index: index,
        project_publication_id: version,
        label: meta?.label ?? null,
        question_type: meta?.question_type ?? null,
        topic: meta?.topic ?? null,
        touches: 0,
        sessions: 0,
        correct: 0,
      });
    }
  }
  return [...byIndex.values()].sort((a, b) => a.item_index - b.item_index);
};

/** The weakest question; questions with very few answers only count when no other does. */
export const hardestItem = (
  items: AddonItemStat[]
): { item: AddonItemStat; rate: number } | null => {
  const rated = items.flatMap((item) => {
    const rate = itemRate(item);
    return rate == null ? [] : [{ item, rate }];
  });
  const solid = rated.filter((r) => r.item.touches >= LOW_SAMPLE);
  return (solid.length > 0 ? solid : rated).reduce<{
    item: AddonItemStat;
    rate: number;
  } | null>((min, r) => (min == null || r.rate < min.rate ? r : min), null);
};
